import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL, AUTH_TOKEN_KEY, ADMIN_TOKEN_KEY } from './constants';
import type { ApiResponse } from './types/common';

export { AUTH_TOKEN_KEY, ADMIN_TOKEN_KEY };

/**
 * Token utility functions (user/Privy token)
 */
export const tokenUtils = {
  getToken: (): string | null => localStorage.getItem(AUTH_TOKEN_KEY),
  setToken: (token: string): void => localStorage.setItem(AUTH_TOKEN_KEY, token),
  removeToken: (): void => localStorage.removeItem(AUTH_TOKEN_KEY),
};

/**
 * Admin token utility functions (separate from user session)
 */
export const adminTokenUtils = {
  getToken: (): string | null => localStorage.getItem(ADMIN_TOKEN_KEY),
  setToken: (token: string): void => localStorage.setItem(ADMIN_TOKEN_KEY, token),
  removeToken: (): void => localStorage.removeItem(ADMIN_TOKEN_KEY),
  hasToken: (): boolean => !!localStorage.getItem(ADMIN_TOKEN_KEY),
};

/**
 * Create axios instance with base configuration and response interceptors
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 100000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - attach Bearer token from localStorage
  instance.interceptors.request.use(
    (config: AxiosRequestConfig): any => {
      const token = tokenUtils.getToken();
      
      // Attach Bearer token if available
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - transform responses and handle errors consistently
  instance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      const apiResponse: ApiResponse<any> = {
        ...response.data,
      };

      response.data = apiResponse;
      return response;
    },
    (error: AxiosError): Promise<never> => {
      if (axios.isAxiosError(error)) {
        // Handle authentication errors
        if (error.response?.status === 401) {
          tokenUtils.removeToken();
          localStorage.removeItem(AUTH_TOKEN_KEY);
          throw new Error('Unauthorized');
        }

        // Handle network errors
        if (!error.response) {
          throw new Error('Network error. Please check your internet connection and try again.');
        }

        // Handle server errors
        if (error.response.status >= 500) {
          throw new Error('Server error. Please try again later or contact support if the problem persists.');
        }

        // Handle client errors
        if (error.response.status >= 400) {
          const errorData = error.response.data as any;
          const errorMessage = 
            errorData?.message ||
            'Bad request. Please check your input and try again.';
          throw new Error(errorMessage);
        }

        const errorData = error.response.data as any;
        throw new Error(
          errorData?.message ||
          error.message ||
          'An error occurred while making the request'
        );
      }
      
      throw new Error('An unexpected error occurred. Please try again.');
    }
  );

  return instance;
};

// Export the configured axios instance
export const apiClient = createAxiosInstance();

/**
 * Admin API client - uses admin token for admin-only endpoints
 */
const createAdminAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 100000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config: AxiosRequestConfig): any => {
      const token = adminTokenUtils.getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (e) => Promise.reject(e)
  );

  instance.interceptors.response.use(
    (res: AxiosResponse) => {
      res.data = { ...res.data };
      return res;
    },
    (error: AxiosError) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        adminTokenUtils.removeToken();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const adminApiClient = createAdminAxiosInstance();

// Export types for better TypeScript support
export type { AxiosResponse, AxiosError, AxiosRequestConfig };
